import { get } from 'svelte/store'

import { getConfig } from './config.svelte'
import { queryClient } from './svelte-query-client'
import { datasetStore, loadDatasets } from '../routes/data-qa/datasets/datasets-store.svelte'
import { loadOrganizations, organizationsStore } from '../routes/organizations/organizations-store'
import {
  loadStorageConfig,
  storageConfigStore,
} from '../routes/storage-configs/storage-configs-store'
import { loggedInStore } from '../stores/logged-in-store'
import { loadUsers, usersStore } from '../stores/user-store'
import { datasetQARunsQueryKey, loadDatasetQARuns } from '@routes/data-qa/runs/dataset-qa-runs'
import { llmsStore, loadLlms } from '@routes/llm/llms/llms-store.svelte'
import { loadModels, modelStore } from '@routes/unlearn-data/models/models-store.svelte'
import {
  loadUnlearningRuns,
  unlearningRunsQueryKey,
} from '@routes/unlearn-data/runs/unlearning-runs'
import { localStoragePrefixes } from '@stores/local-storage.svelte'

export const checkAsyncState = (asyncState: { loading: boolean; error: string }) => {
  if (asyncState.loading) {
    return 'Loading...'
  } else if (asyncState.error) {
    return `Error: ${asyncState.error}`
  }
  return null
}

interface IDataToLoad {
  models: boolean
  llms: boolean
  unlearningRuns: boolean
  dataQADatasets: boolean
  dataQARuns: boolean
  storageConfigs: boolean
  organizations: boolean
}

export const loadData = async ({
  models = false,
  llms = false,
  unlearningRuns = false,
  dataQADatasets = false,
  dataQARuns = false,
  storageConfigs = true,
  organizations = true,
}: Partial<IDataToLoad>) => {
  const promises: Promise<void>[] = []

  if (get(modelStore).size === 0 && models) {
    promises.push(loadModels())
  }
  if (get(llmsStore).size === 0 && llms) {
    promises.push(loadLlms())
  }
  if (queryClient.getQueryData(unlearningRunsQueryKey(false)) === undefined && unlearningRuns) {
    promises.push(loadUnlearningRuns())
  }
  if (get(datasetStore).size === 0 && dataQADatasets) {
    promises.push(loadDatasets())
  }
  if (queryClient.getQueryData(datasetQARunsQueryKey(false)) === undefined && dataQARuns) {
    promises.push(loadDatasetQARuns())
  }
  if (get(storageConfigStore).size === 0 && storageConfigs) {
    promises.push(loadStorageConfig())
  }
  if (get(organizationsStore).size === 0 && organizations) {
    promises.push(loadOrganizations())
  }
  if (get(usersStore).length === 0 || get(loggedInStore).userId === 0) {
    promises.push(loadUsers())
  }
  const config = await getConfig()
  if (config.airGapped) {
    promises.push(localStoragePrefixes.getPrefixesFromServer())
  }

  await Promise.all(promises)
}

export const enum ClassesOption {
  AutoDetection = '1',
  ManualInput = '2',
}

export const throttle = <Args extends unknown[], ReturnValue>(
  func: (...args: Args) => ReturnValue,
  limit: number,
) => {
  let inThrottle = false

  return function (this: ThisParameterType<typeof func>, ...args: Args): void {
    if (!inThrottle) {
      void func.apply(this, args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}
